// dashboard.layout — susunan widget dashboard yang bisa diatur pengguna (urutan + sembunyikan).
//
// Disimpan per pengguna di localStorage: ringan, tanpa migrasi, tanpa batasan tulis-settings
// (yang hanya untuk O/M). Konsekuensi jujur: setelan ini per-perangkat, belum ikut antar-HP/PC.
// Sinkron lintas perangkat menyusul bila diperlukan (pindah ke settings).
//
// Logika susunan (urut/sembunyi/geser) dipisah di sini sebagai fungsi MURNI supaya bisa diuji
// tanpa DOM — bagian yang paling mudah salah saat orang menggeser widget.

export type JenisGrafik = 'bar' | 'line' | 'pie';

export interface DashLayout {
  /** Urutan widget menurut id. Id yang tak ada di sini diletakkan di akhir (widget baru muncul sendiri). */
  urut: string[];
  /** Id yang disembunyikan. */
  sembunyi: string[];
  /** Jenis grafik tren 12 bulan. */
  grafik?: JenisGrafik;
  /** Jenis grafik "Servis per Status". */
  grafikStatus?: JenisGrafik;
}

const KEY = (userId: number | string): string => `ifics_dash_${userId}`;

export function bacaLayout(userId: number | string): DashLayout {
  try {
    const raw = localStorage.getItem(KEY(userId));
    if (!raw) return { urut: [], sembunyi: [] };
    const p = JSON.parse(raw) as Partial<DashLayout>;
    return { urut: Array.isArray(p.urut) ? p.urut : [], sembunyi: Array.isArray(p.sembunyi) ? p.sembunyi : [], grafik: p.grafik, grafikStatus: p.grafikStatus };
  } catch { return { urut: [], sembunyi: [] }; }
}

export function simpanLayout(userId: number | string, l: DashLayout): void {
  try { localStorage.setItem(KEY(userId), JSON.stringify(l)); } catch { /* penyimpanan penuh/diblok → biarkan default */ }
}

/**
 * Urutkan daftar widget TERSEDIA menurut layout. Widget baru (tak disebut di `urut`) selalu ikut
 * muncul di akhir — jadi menambah widget di kode tak pernah "hilang" karena layout lama.
 */
export function terapkanUrut<T extends { id: string }>(tersedia: T[], urut: string[]): T[] {
  const pos = new Map(urut.map((id, i) => [id, i]));
  return [...tersedia].sort((a, b) => (pos.get(a.id) ?? 1e9) - (pos.get(b.id) ?? 1e9)
    || tersedia.indexOf(a) - tersedia.indexOf(b));
}

/** Geser satu id ke atas/bawah dalam daftar urut yang SUDAH terpakai (id nyata, bukan indeks). */
export function geser(urutTampil: string[], id: string, arah: -1 | 1): string[] {
  const i = urutTampil.indexOf(id);
  const j = i + arah;
  if (i < 0 || j < 0 || j >= urutTampil.length) return urutTampil;
  const out = [...urutTampil];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/** Pindahkan `id` ke posisi indeks `to` dalam daftar urut yang SUDAH terpakai (untuk drag-n-drop). */
export function pindahKe(urutTampil: string[], id: string, to: number): string[] {
  const i = urutTampil.indexOf(id);
  if (i < 0) return urutTampil;
  const out = [...urutTampil];
  out.splice(i, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, id);
  return out;
}

/**
 * Susun layout dari sebuah "gaya/model": tampilkan HANYA id di `pilih` (urutan = pilih),
 * sembunyikan semua id lain yang tersedia. Preferensi lain (grafik/grafikStatus) dipertahankan.
 * Dipakai pemilih Gaya Dashboard supaya tiap model = tata-letak terkurasi tanpa duplikasi.
 */
export function layoutDariModel(semuaId: string[], pilih: string[], prev: DashLayout): DashLayout {
  return { ...prev, urut: [...pilih], sembunyi: semuaId.filter((id) => !pilih.includes(id)) };
}

export const tampil = (l: DashLayout, id: string): boolean => !l.sembunyi.includes(id);

export function setTampil(l: DashLayout, id: string, on: boolean): DashLayout {
  const set = new Set(l.sembunyi);
  if (on) set.delete(id); else set.add(id);
  return { ...l, sembunyi: [...set] };
}
